import { useState, useRef, useEffect } from 'react'
import { useSearchParams, useBlocker } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTopBar } from '@/contexts/TopBarContext'
import { adminApi, type SignatureType, type TenantEmailTemplateDto } from '@/api/admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import UnsavedBar from '@/components/shared/UnsavedBar'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Upload, Check, X, Plus, PenLine, Lock, Link2, Unlink, ExternalLink, RefreshCw } from 'lucide-react'
import ProUpgradeDialog from '@/components/shared/ProUpgradeDialog'
import { FONT_OPTIONS, buildBrandStyle, isHeaderLight, useBrandFont, type BrandInput } from '@/lib/brandTheme'
import VenuesPage from '@/features/admin/venues/VenuesPage'
import RosterImporter from '@/features/admin/import/RosterImporter'
import PortalDefaultsCard from '@/features/admin/portal/PortalDefaultsCard'
import PhotoSettingsCard from '@/features/photos/PhotoSettingsCard'
import LiveSettingsCard from '@/features/live/LiveSettingsCard'
import { useAdminDates } from '@/hooks/useDates'
import { addressLabels } from '@/lib/address'
import { isUs } from '@/lib/dates'

const DEFAULT_ADMIN_DAYS = [60, 30, 14]
const DEFAULT_CLIENT_DAYS = [60, 30, 14]

type BrandingFields = {
  displayName: string
  primaryColour: string
  secondaryColour: string
  headerStyle: string
  cornerStyle: string
  fontFamily: string
}
type ProfileFields = {
  companyNumber: string
  vatNumber: string
  addressLine1: string
  addressLine2: string
  city: string
  county: string
  postcode: string
  country: string
  contactEmail: string
  website: string
}
type InvoicingFields = {
  taxLabel: string
  defaultVatRate: string
  routingNumber: string
  bankName: string
  accountName: string
  accountNumber: string
  sortCode: string
  iban: string
  paymentTermsDays: string
  paymentReference: string
  paymentLink: string
  acceptInvoicePayments: boolean
  acceptSessionPayments: boolean
  requireSessionPaymentToBook: boolean
  passFeeToCustomerForInvoices: boolean
  passFeeToCustomerForSessions: boolean
}

function ColourField({ label, value, onChange, hint }: {
  label: string
  value: string
  onChange: (v: string) => void
  hint?: string
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        <input
          type="color"
          value={value || '#4F46E5'}
          onChange={e => onChange(e.target.value)}
          className="h-9 w-14 cursor-pointer rounded border border-input p-0.5"
        />
        <Input
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="#4F46E5"
          className="font-mono w-32"
        />
      </div>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  )
}

function SegmentedField({ label, value, options, onChange, hint }: {
  label: string
  value: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  hint?: string
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50">
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`px-3 py-1.5 text-sm rounded-[5px] transition-colors cursor-pointer ${
              value === opt.value ? 'bg-white shadow-sm font-medium text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  )
}

function BrandPreview({ brand, displayName, logoUrl }: {
  brand: BrandInput
  displayName: string
  logoUrl?: string | null
}) {
  useBrandFont(brand.fontFamily)
  const style = buildBrandStyle(brand)
  const light = isHeaderLight(brand)

  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden" style={style}>
      {/* Mini header */}
      <div
        className={`flex items-center justify-between px-4 py-3 ${light ? 'bg-white border-b border-gray-200' : ''}`}
        style={light ? undefined : { backgroundColor: 'var(--brand-primary)' }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {logoUrl && <img src={logoUrl} alt="" className="h-6 max-w-20 object-contain" />}
          <span
            className="text-sm font-bold truncate"
            style={{ color: light ? '#111111' : 'var(--brand-primary-contrast)' }}
          >
            {displayName || 'Your Company'}
          </span>
        </div>
        <div
          className="flex items-center gap-3 text-xs font-medium shrink-0"
          style={{ color: light ? '#6b7280' : 'var(--brand-primary-contrast)', opacity: light ? 1 : 0.75 }}
        >
          <span>Overview</span>
          <span
            className="px-2 py-1 rounded-[var(--brand-radius-sm)]"
            style={{
              backgroundColor: 'var(--brand-secondary)',
              color: 'var(--brand-secondary-contrast)',
              opacity: 1,
            }}
          >
            My Team
          </span>
          <span>Bookings</span>
        </div>
      </div>

      {/* Mini body */}
      <div className="p-4 space-y-3 bg-slate-50">
        <div className="bg-white border border-gray-200 rounded-[var(--brand-radius)] p-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">Sample training card</p>
            <p className="text-xs text-gray-400 mt-0.5">This is how cards will look</p>
          </div>
          <button
            type="button"
            tabIndex={-1}
            className="text-xs font-medium px-3 py-1.5 rounded-[var(--brand-radius-sm)] shrink-0 cursor-default"
            style={{ backgroundColor: 'var(--brand-primary)', color: 'var(--brand-primary-contrast)' }}
          >
            Action
          </button>
        </div>
        <span
          className="inline-block text-xs font-semibold px-2 py-1 rounded-[var(--brand-radius-full)]"
          style={{ backgroundColor: 'var(--brand-secondary)', color: 'var(--brand-secondary-contrast)' }}
        >
          Accent badge
        </span>
      </div>
    </div>
  )
}

function TextField({ id, label, value, onChange, placeholder, type = 'text' }: {
  id: string
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  type?: string
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  )
}

function DaysTagInput({ label, hint, days, onChange }: {
  label: string
  hint: string
  days: number[]
  onChange: (d: number[]) => void
}) {
  const [inputVal, setInputVal] = useState('')

  const addDay = () => {
    const n = parseInt(inputVal.trim())
    if (!isNaN(n) && n > 0 && n <= 365 && !days.includes(n)) {
      onChange([...days, n].sort((a, b) => b - a))
    }
    setInputVal('')
  }

  const removeDay = (d: number) => onChange(days.filter(x => x !== d))

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex flex-wrap gap-1.5 min-h-9 p-2 border border-gray-200 rounded-md bg-white items-center">
        {days.length === 0 && (
          <span className="text-xs text-gray-400 px-1">No reminders set</span>
        )}
        {[...days].sort((a, b) => b - a).map(d => (
          <span key={d} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700 border border-indigo-100">
            {d} days
            <button type="button" onClick={() => removeDay(d)} className="hover:text-indigo-900 cursor-pointer">
              <X size={10} />
            </button>
          </span>
        ))}
        <div className="flex items-center gap-1 ml-auto shrink-0">
          <Input
            type="number"
            min={1}
            max={365}
            value={inputVal}
            onChange={e => setInputVal(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDay() } }}
            placeholder="Days"
            className="h-7 w-20 text-sm"
          />
          <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={addDay}>
            <Plus size={12} />
          </Button>
        </div>
      </div>
      <p className="text-xs text-gray-400">{hint}</p>
    </div>
  )
}

export function SignatureEditor({
  signatureType,
  signatureText,
  signatureImageUrl,
  isPending,
  onSave,
  onUpload,
  label = 'Signature',
  hint,
}: {
  signatureType: SignatureType
  signatureText: string | null
  signatureImageUrl: string | null
  isPending: boolean
  onSave: (type: SignatureType, text: string | null) => void
  onUpload: (file: File) => void
  label?: string
  hint?: string
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [localType, setLocalType] = useState<SignatureType>(signatureType)
  const [localText, setLocalText] = useState(signatureText ?? '')
  const [saved, setSaved] = useState(false)

  const handleSave = () => {
    onSave(localType, localType === 'Text' ? localText : null)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  return (
    <div className="space-y-4">
      {hint && <p className="text-sm text-gray-500">{hint}</p>}

      <div className="flex gap-4">
        {(['None', 'Image', 'Text'] as SignatureType[]).map(t => (
          <label key={t} className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            <input
              type="radio"
              name={`sig-type-${label}`}
              value={t}
              checked={localType === t}
              onChange={() => setLocalType(t)}
              className="accent-indigo-600"
            />
            {t === 'None' ? 'No signature' : t === 'Image' ? 'Image upload' : 'Typed (script font)'}
          </label>
        ))}
      </div>

      {localType === 'Image' && (
        <div className="space-y-3">
          {signatureImageUrl ? (
            <div className="flex items-center gap-4 flex-wrap">
              <img
                src={signatureImageUrl}
                alt={label}
                className="h-16 w-auto object-contain rounded border border-gray-200 p-2 bg-white max-w-xs"
              />
              <Button type="button" variant="outline" size="sm" className="gap-1.5"
                onClick={() => fileInputRef.current?.click()}
                disabled={isPending}>
                <Upload size={14} /> {isPending ? 'Uploading…' : 'Replace image'}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="h-16 w-36 rounded border-2 border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400">
                No image
              </div>
              <Button type="button" variant="outline" size="sm" className="gap-1.5"
                onClick={() => fileInputRef.current?.click()}
                disabled={isPending}>
                <Upload size={14} /> {isPending ? 'Uploading…' : 'Upload image'}
              </Button>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f); e.target.value = '' }}
          />
          <p className="text-xs text-gray-400">PNG, JPG or WebP. Transparent background recommended.</p>
        </div>
      )}

      {localType === 'Text' && (
        <div className="space-y-2">
          <Label>Signature text</Label>
          <div className="flex items-center gap-3">
            <Input
              value={localText}
              onChange={e => setLocalText(e.target.value)}
              placeholder="e.g. Jane Smith"
              className="max-w-xs"
            />
            <span className="text-sm text-gray-400 italic" style={{ fontFamily: '"Dancing Script", cursive', fontSize: '1.2rem' }}>
              {localText || 'Preview'}
            </span>
          </div>
          <p className="text-xs text-gray-400">Rendered in Dancing Script font on the certificate.</p>
        </div>
      )}

      <div className="flex items-center gap-3 pt-1">
        <Button type="button" size="sm" onClick={handleSave} disabled={isPending}>
          {isPending ? 'Saving…' : `Save ${label.toLowerCase()}`}
        </Button>
        {saved && (
          <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
            <Check size={14} /> Saved
          </span>
        )}
      </div>
    </div>
  )
}

export default function MyCompanyPage() {
  const { formatDate } = useAdminDates()
  useTopBar(<span className="text-sm font-medium text-gray-900">My Company</span>)
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const logoInputRef = useRef<HTMLInputElement>(null)
  const [brandingSaved, setBrandingSaved] = useState(false)
  const [profileSaved, setProfileSaved] = useState(false)
  const [invoicingSaved, setInvoicingSaved] = useState(false)
  const brandingSavedRef = useRef<BrandingFields | null>(null)
  const profileSavedRef = useRef<ProfileFields | null>(null)
  const invoicingSavedRef = useRef<InvoicingFields | null>(null)
  const [reminderSaved, setReminderSaved] = useState(false)
  const [initialised, setInitialised] = useState(false)
  const [showProDialog, setShowProDialog] = useState(false)
  const [proFeature, setProFeature] = useState<string | null>(null)

  const [displayName, setDisplayName] = useState('')
  const [primaryColour, setPrimaryColour] = useState('#4F46E5')
  const [secondaryColour, setSecondaryColour] = useState('')
  const [headerStyle, setHeaderStyle] = useState('Dark')
  const [cornerStyle, setCornerStyle] = useState('Rounded')
  const [fontFamily, setFontFamily] = useState('System')
  const [companyNumber, setCompanyNumber] = useState('')
  const [vatNumber, setVatNumber] = useState('')
  const [addressLine1, setAddressLine1] = useState('')
  const [addressLine2, setAddressLine2] = useState('')
  const [city, setCity] = useState('')
  const [county, setCounty] = useState('')
  const [postcode, setPostcode] = useState('')
  const [country, setCountry] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [website, setWebsite] = useState('')
  const [bankName, setBankName] = useState('')
  const [accountName, setAccountName] = useState('')
  const [accountNumber, setAccountNumber] = useState('')
  const [sortCode, setSortCode] = useState('')
  const [iban, setIban] = useState('')
  const [routingNumber, setRoutingNumber] = useState('')
  const [taxLabel, setTaxLabel] = useState('VAT')
  const [paymentTermsDays, setPaymentTermsDays] = useState('')
  const [paymentReference, setPaymentReference] = useState('')
  const [defaultVatRate, setDefaultVatRate] = useState('')
  const [paymentLink, setPaymentLink] = useState('')
  const [acceptInvoicePayments, setAcceptInvoicePayments] = useState(true)
  const [acceptSessionPayments, setAcceptSessionPayments] = useState(true)
  const [requireSessionPaymentToBook, setRequireSessionPaymentToBook] = useState(false)
  const [passFeeToCustomerForInvoices, setPassFeeToCustomerForInvoices] = useState(false)
  const [passFeeToCustomerForSessions, setPassFeeToCustomerForSessions] = useState(false)
  const [xeroAccountCode, setXeroAccountCode] = useState('200')
  const [xeroAccountSaved, setXeroAccountSaved] = useState(false)
  const [xeroOutputTaxType, setXeroOutputTaxType] = useState('')
  const [adminDays, setAdminDays] = useState<number[]>(DEFAULT_ADMIN_DAYS)
  const [clientDays, setClientDays] = useState<number[]>(DEFAULT_CLIENT_DAYS)
  const [feedbackSaved, setFeedbackSaved] = useState(false)
  const [feedbackEnabled, setFeedbackEnabled] = useState(true)
  const [feedbackTrainerVisible, setFeedbackTrainerVisible] = useState(false)
  const [feedbackAllowEdit, setFeedbackAllowEdit] = useState(false)
  const [feedbackWindowDays, setFeedbackWindowDays] = useState('')
  const [feedbackNotifyClient, setFeedbackNotifyClient] = useState(true)
  const [editingTemplate, setEditingTemplate] = useState<string | null>(null)
  const [templateSubject, setTemplateSubject] = useState('')
  const [templateBody, setTemplateBody] = useState('')
  const [templateSaved, setTemplateSaved] = useState(false)

  const { data: profile, isLoading } = useQuery({
    queryKey: ['company-profile'],
    queryFn: () => adminApi.getCompanyProfile().then(r => r.data),
  })

  const isPro = profile?.planTier === 'Pro'
  const currency = profile?.currency ?? 'GBP'
  const isUsd = currency === 'USD'
  const labels = addressLabels(profile?.region)
  const usRegion = isUs(profile?.region)

  if (profile && !initialised) {
    const initBranding: BrandingFields = {
      displayName: profile.displayName ?? '',
      primaryColour: profile.primaryColour ?? '#4F46E5',
      secondaryColour: profile.secondaryColour ?? '',
      headerStyle: profile.headerStyle ?? 'Dark',
      cornerStyle: profile.cornerStyle ?? 'Rounded',
      fontFamily: profile.fontFamily ?? 'System',
    }
    const initProfile: ProfileFields = {
      companyNumber: profile.companyNumber ?? '',
      vatNumber: profile.vatNumber ?? '',
      addressLine1: profile.addressLine1 ?? '',
      addressLine2: profile.addressLine2 ?? '',
      city: profile.city ?? '',
      county: profile.county ?? '',
      postcode: profile.postcode ?? '',
      country: profile.country ?? '',
      contactEmail: profile.contactEmail ?? '',
      website: profile.website ?? '',
    }
    const initInvoicing: InvoicingFields = {
      taxLabel: profile.taxLabel ?? 'VAT',
      routingNumber: profile.routingNumber ?? '',
      defaultVatRate: profile.defaultVatRate?.toString() ?? '',
      bankName: profile.bankName ?? '',
      accountName: profile.accountName ?? '',
      accountNumber: profile.accountNumber ?? '',
      sortCode: profile.sortCode ?? '',
      iban: profile.iban ?? '',
      paymentTermsDays: profile.paymentTermsDays?.toString() ?? '',
      paymentReference: profile.paymentReference ?? '',
      paymentLink: profile.paymentLink ?? '',
      acceptInvoicePayments: profile.acceptInvoicePayments ?? true,
      acceptSessionPayments: profile.acceptSessionPayments ?? true,
      requireSessionPaymentToBook: profile.requireSessionPaymentToBook ?? false,
      passFeeToCustomerForInvoices: profile.passFeeToCustomerForInvoices ?? false,
      passFeeToCustomerForSessions: profile.passFeeToCustomerForSessions ?? false,
    }

    setDisplayName(initBranding.displayName)
    setPrimaryColour(initBranding.primaryColour)
    setSecondaryColour(initBranding.secondaryColour)
    setHeaderStyle(initBranding.headerStyle)
    setCornerStyle(initBranding.cornerStyle)
    setFontFamily(initBranding.fontFamily)
    setCompanyNumber(initProfile.companyNumber)
    setVatNumber(initProfile.vatNumber)
    setAddressLine1(initProfile.addressLine1)
    setAddressLine2(initProfile.addressLine2)
    setCity(initProfile.city)
    setCounty(initProfile.county)
    setPostcode(initProfile.postcode)
    setCountry(initProfile.country)
    setContactEmail(initProfile.contactEmail)
    setWebsite(initProfile.website)
    setBankName(initInvoicing.bankName)
    setAccountName(initInvoicing.accountName)
    setAccountNumber(initInvoicing.accountNumber)
    setSortCode(initInvoicing.sortCode)
    setIban(initInvoicing.iban)
    setRoutingNumber(initInvoicing.routingNumber)
    setTaxLabel(initInvoicing.taxLabel)
    setPaymentTermsDays(initInvoicing.paymentTermsDays)
    setPaymentReference(initInvoicing.paymentReference)
    setDefaultVatRate(initInvoicing.defaultVatRate)
    setPaymentLink(initInvoicing.paymentLink)
    setAcceptInvoicePayments(initInvoicing.acceptInvoicePayments)
    setAcceptSessionPayments(initInvoicing.acceptSessionPayments)
    setRequireSessionPaymentToBook(initInvoicing.requireSessionPaymentToBook)
    setPassFeeToCustomerForInvoices(initInvoicing.passFeeToCustomerForInvoices)
    setPassFeeToCustomerForSessions(initInvoicing.passFeeToCustomerForSessions)

    brandingSavedRef.current = initBranding
    profileSavedRef.current = initProfile
    invoicingSavedRef.current = initInvoicing

    setAdminDays(profile.adminReminderDays ?? DEFAULT_ADMIN_DAYS)
    setClientDays(profile.clientReminderDays ?? DEFAULT_CLIENT_DAYS)
    setFeedbackEnabled(profile.feedbackEnabled ?? true)
    setFeedbackTrainerVisible(profile.feedbackTrainerVisible ?? false)
    setFeedbackAllowEdit(profile.feedbackAllowEdit ?? false)
    setFeedbackWindowDays(profile.feedbackWindowDays?.toString() ?? '')
    setFeedbackNotifyClient(profile.feedbackNotifyClient ?? true)
    setInitialised(true)
  }

  // The backend always takes the full profile in one PUT, so every tab's mutation posts the
  // same complete payload — they're only "separate" in that each tracks its own pending/success
  // state and marks only its own tab's fields as saved, rather than sharing one mutation.
  const buildProfilePayload = () => ({
    displayName: displayName || null,
    primaryColour: primaryColour || null,
    secondaryColour: secondaryColour || null,
    headerStyle,
    cornerStyle,
    fontFamily,
    companyNumber: companyNumber || null,
    vatNumber: vatNumber || null,
    addressLine1: addressLine1 || null,
    addressLine2: addressLine2 || null,
    city: city || null,
    county: county || null,
    postcode: postcode || null,
    country: country || null,
    contactEmail: contactEmail || null,
    website: website || null,
    bankName: bankName || null,
    accountName: accountName || null,
    accountNumber: accountNumber || null,
    sortCode: sortCode || null,
    iban: iban || null,
    routingNumber,
    taxLabel: taxLabel.trim() || 'VAT',
    paymentTermsDays: paymentTermsDays ? parseInt(paymentTermsDays) : null,
    paymentReference: paymentReference || null,
    defaultVatRate: defaultVatRate ? parseFloat(defaultVatRate) : null,
    paymentLink: paymentLink || null,
    acceptInvoicePayments,
    acceptSessionPayments,
    requireSessionPaymentToBook,
    passFeeToCustomerForInvoices,
    passFeeToCustomerForSessions,
  })

  const brandingFields: BrandingFields = { displayName, primaryColour, secondaryColour, headerStyle, cornerStyle, fontFamily }
  const profileFields: ProfileFields = { companyNumber, vatNumber, addressLine1, addressLine2, city, county, postcode, country, contactEmail, website }
  const invoicingFields: InvoicingFields = { taxLabel, routingNumber, defaultVatRate, bankName, accountName, accountNumber, sortCode, iban, paymentTermsDays, paymentReference, paymentLink, acceptInvoicePayments, acceptSessionPayments, requireSessionPaymentToBook, passFeeToCustomerForInvoices, passFeeToCustomerForSessions }

  const isBrandingDirty = brandingSavedRef.current !== null && JSON.stringify(brandingFields) !== JSON.stringify(brandingSavedRef.current)
  const isProfileDirty = profileSavedRef.current !== null && JSON.stringify(profileFields) !== JSON.stringify(profileSavedRef.current)
  const isInvoicingDirty = invoicingSavedRef.current !== null && JSON.stringify(invoicingFields) !== JSON.stringify(invoicingSavedRef.current)
  const hasUnsavedChanges = isBrandingDirty || isProfileDirty || isInvoicingDirty

  function cancelBranding() {
    if (!brandingSavedRef.current) return
    const s = brandingSavedRef.current
    setDisplayName(s.displayName); setPrimaryColour(s.primaryColour); setSecondaryColour(s.secondaryColour)
    setHeaderStyle(s.headerStyle); setCornerStyle(s.cornerStyle); setFontFamily(s.fontFamily)
  }
  function cancelProfile() {
    if (!profileSavedRef.current) return
    const s = profileSavedRef.current
    setCompanyNumber(s.companyNumber); setVatNumber(s.vatNumber)
    setAddressLine1(s.addressLine1); setAddressLine2(s.addressLine2)
    setCity(s.city); setCounty(s.county); setPostcode(s.postcode); setCountry(s.country)
    setContactEmail(s.contactEmail); setWebsite(s.website)
  }
  function cancelInvoicing() {
    if (!invoicingSavedRef.current) return
    const s = invoicingSavedRef.current
    setDefaultVatRate(s.defaultVatRate); setBankName(s.bankName); setAccountName(s.accountName)
    setAccountNumber(s.accountNumber); setSortCode(s.sortCode); setIban(s.iban)
    setRoutingNumber(s.routingNumber); setTaxLabel(s.taxLabel)
    setPaymentTermsDays(s.paymentTermsDays); setPaymentReference(s.paymentReference); setPaymentLink(s.paymentLink)
    setAcceptInvoicePayments(s.acceptInvoicePayments); setAcceptSessionPayments(s.acceptSessionPayments)
    setRequireSessionPaymentToBook(s.requireSessionPaymentToBook)
    setPassFeeToCustomerForInvoices(s.passFeeToCustomerForInvoices)
    setPassFeeToCustomerForSessions(s.passFeeToCustomerForSessions)
  }

  // Browser-level guard: catches tab close / refresh / URL bar navigation.
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = '' }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [hasUnsavedChanges])

  // In-app guard: catches navigating to a different page via the router.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  )
  useEffect(() => {
    if (blocker.state !== 'blocked') return
    if (window.confirm('You have unsaved changes on this page. Leave without saving?')) {
      blocker.proceed()
    } else {
      blocker.reset()
    }
  }, [blocker])

  const brandingMutation = useMutation({
    mutationFn: () => adminApi.updateCompanyProfile(buildProfilePayload()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-profile'] })
      brandingSavedRef.current = brandingFields
      setBrandingSaved(true)
      setTimeout(() => setBrandingSaved(false), 2500)
    },
  })

  const profileMutation = useMutation({
    mutationFn: () => adminApi.updateCompanyProfile(buildProfilePayload()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-profile'] })
      profileSavedRef.current = profileFields
      setProfileSaved(true)
      setTimeout(() => setProfileSaved(false), 2500)
    },
  })

  const invoicingMutation = useMutation({
    mutationFn: () => adminApi.updateCompanyProfile(buildProfilePayload()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-profile'] })
      invoicingSavedRef.current = invoicingFields
      setInvoicingSaved(true)
      setTimeout(() => setInvoicingSaved(false), 2500)
    },
  })

  const reminderMutation = useMutation({
    mutationFn: () => adminApi.updateReminderSchedule({
      adminReminderDays: adminDays.length > 0 ? adminDays : null,
      clientReminderDays: clientDays.length > 0 ? clientDays : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-profile'] })
      setReminderSaved(true)
      setTimeout(() => setReminderSaved(false), 2500)
    },
  })

  const logoMutation = useMutation({
    mutationFn: (file: File) => adminApi.uploadTenantLogo(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-profile'] }),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Logo upload failed'
      alert(msg)
    },
  })

  const termsFileInputRef = useRef<HTMLInputElement>(null)

  const termsMutation = useMutation({
    mutationFn: (file: File) => adminApi.uploadTenantTerms(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-profile'] }),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Upload failed'
      alert(msg)
    },
  })

  const viewTerms = async () => {
    const res = await adminApi.getTenantTermsDocument()
    const url = URL.createObjectURL(new Blob([res.data as BlobPart], { type: 'application/pdf' }))
    window.open(url, '_blank')
  }

  const companySignatureMutation = useMutation({
    mutationFn: ({ type, text }: { type: SignatureType; text: string | null }) =>
      adminApi.updateCompanySignature({ signatureType: type, signatureText: text }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-profile'] }),
  })

  const companySignatureImageMutation = useMutation({
    mutationFn: (file: File) => adminApi.uploadCompanySignatureImage(file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['company-profile'] }),
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Signature upload failed'
      alert(msg)
    },
  })

  const feedbackMutation = useMutation({
    mutationFn: () => adminApi.updateFeedbackSettings({
      enabled: feedbackEnabled,
      trainerVisible: feedbackTrainerVisible,
      allowEdit: feedbackAllowEdit,
      windowDays: feedbackWindowDays ? parseInt(feedbackWindowDays) : null,
      notifyClient: feedbackNotifyClient,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-profile'] })
      setFeedbackSaved(true)
      setTimeout(() => setFeedbackSaved(false), 2500)
    },
  })

  const { data: xeroStatus, refetch: refetchXero } = useQuery({
    queryKey: ['xero-status'],
    queryFn: () => adminApi.getXeroStatus().then(r => r.data),
  })

  // Seed the Xero inputs from the saved connection (they previously always started at the default).
  const xeroConn = xeroStatus?.connection
  useEffect(() => {
    if (xeroConn) {
      setXeroAccountCode(xeroConn.accountCode)
      setXeroOutputTaxType(xeroConn.outputTaxType ?? '')
    }
  }, [xeroConn?.accountCode, xeroConn?.outputTaxType])

  const { data: stripeStatus, refetch: refetchStripe } = useQuery({
    queryKey: ['stripe-connect-status'],
    queryFn: () => adminApi.getStripeConnectStatus().then(r => r.data),
  })

  // Read ?xero= query param set by the OAuth callback redirect
  const xeroCallbackResult = searchParams.get('xero')
  const xeroCallbackMessage = searchParams.get('message')

  const connectXeroMutation = useMutation({
    mutationFn: async () => {
      const { data } = await adminApi.getXeroConnectUrl()
      window.location.href = data.url
    },
  })

  const disconnectXeroMutation = useMutation({
    mutationFn: () => adminApi.disconnectXero(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['xero-status'] })
      refetchXero()
    },
  })

  // Read ?stripe= query param set when Stripe returns from hosted onboarding
  const stripeCallbackResult = searchParams.get('stripe')

  const connectStripeMutation = useMutation({
    mutationFn: async () => {
      const { data } = await adminApi.startStripeOnboarding()
      window.location.href = data.url
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } }; message?: string }
      alert(err?.response?.data?.error ?? err?.message ?? 'Could not start Stripe onboarding')
    },
  })

  const disconnectStripeMutation = useMutation({
    mutationFn: () => adminApi.disconnectStripeConnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-connect-status'] })
      refetchStripe()
    },
  })

  const xeroSettingsMutation = useMutation({
    mutationFn: () => adminApi.updateXeroSettings(xeroAccountCode, xeroOutputTaxType),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['xero-status'] })
      setXeroAccountSaved(true)
      setTimeout(() => setXeroAccountSaved(false), 2500)
    },
  })

  const { data: emailTemplates } = useQuery({
    queryKey: ['email-templates'],
    queryFn: () => adminApi.getEmailTemplates().then(r => r.data),
  })

  const templateMutation = useMutation({
    mutationFn: ({ type, subject, body }: { type: string; subject: string; body: string }) =>
      adminApi.updateEmailTemplate(type, { subject, body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email-templates'] })
      setEditingTemplate(null)
      setTemplateSaved(true)
      setTimeout(() => setTemplateSaved(false), 2500)
    },
  })

  function startEditTemplate(t: TenantEmailTemplateDto) {
    setEditingTemplate(t.type)
    setTemplateSubject(t.subject)
    setTemplateBody(t.body)
    setTemplateSaved(false)
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) logoMutation.mutate(file)
    e.target.value = ''
  }

  if (isLoading) return <p className="text-gray-500">Loading…</p>

  const justSaved = brandingSaved || profileSaved || invoicingSaved

  return (
    <div>
      {justSaved && (
        <div className="fixed bottom-4 right-4 z-20 flex items-center gap-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium px-3 py-2 shadow-lg">
          <Check size={14} /> Saved
        </div>
      )}
      <Tabs
        value={searchParams.get('tab') ?? 'profile'}
        onValueChange={v => setSearchParams(prev => { prev.set('tab', v); prev.delete('xero'); prev.delete('message'); return prev })}
      >
        <div className="flex overflow-x-auto mb-6">
          <TabsList className="shrink-0">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="branding">Branding</TabsTrigger>
            <TabsTrigger value="invoicing">Invoicing</TabsTrigger>
            <TabsTrigger value="certificates">Certificates</TabsTrigger>
            <TabsTrigger value="notifications">Notifications</TabsTrigger>
            <TabsTrigger value="portal">Client portal</TabsTrigger>
            <TabsTrigger value="photos">Photos</TabsTrigger>
            <TabsTrigger value="feedback">Feedback</TabsTrigger>
            <TabsTrigger value="live">Live</TabsTrigger>
            <TabsTrigger value="venues">Venues</TabsTrigger>
            <TabsTrigger value="integrations">Integrations</TabsTrigger>
            <TabsTrigger value="import">Import</TabsTrigger>
          </TabsList>
          <span className="w-4 shrink-0 sm:hidden" aria-hidden="true" />
        </div>

        {/* Profile tab — company details, address, contact */}
        <TabsContent value="profile">
          <form onSubmit={e => { e.preventDefault(); profileMutation.mutate() }} className="space-y-6 max-w-2xl">

            <Card>
              <CardHeader><CardTitle className="text-base">Company Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField id="companyNumber" label="Company number" value={companyNumber}
                    onChange={setCompanyNumber} placeholder="e.g. 12345678" />
                  <TextField id="vatNumber" label={`${taxLabel || 'VAT'} number`} value={vatNumber}
                    onChange={setVatNumber} placeholder={usRegion ? 'e.g. 12-3456789' : 'e.g. GB 123 4567 89'} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Address</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <TextField id="addressLine1" label="Address line 1" value={addressLine1}
                  onChange={setAddressLine1} placeholder="Street / building" />
                <TextField id="addressLine2" label="Address line 2" value={addressLine2}
                  onChange={setAddressLine2} placeholder="Area / district (optional)" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField id="city" label="City / town" value={city} onChange={setCity} placeholder="e.g. Manchester" />
                  <TextField id="county" label={labels.county} value={county} onChange={setCounty} placeholder={labels.countyPlaceholder} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField id="postcode" label={labels.postcode} value={postcode} onChange={setPostcode} placeholder={labels.postcodePlaceholder} />
                  <TextField id="country" label="Country" value={country} onChange={setCountry} placeholder={labels.countryPlaceholder} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Contact</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <TextField id="contactEmail" label="Contact email" value={contactEmail} type="email"
                  onChange={setContactEmail} placeholder="e.g. info@company.com" />
                <TextField id="website" label="Website" value={website}
                  onChange={setWebsite} placeholder="e.g. https://www.company.com" />
              </CardContent>
            </Card>

            <UnsavedBar visible={isProfileDirty} onCancel={cancelProfile} isPending={profileMutation.isPending} />
          </form>

          <Card className="max-w-2xl mt-6">
            <CardHeader><CardTitle className="text-base">Terms &amp; Conditions</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">
                Upload your own Terms &amp; Conditions (PDF). Once uploaded, clients must accept them
                to book a session and the first time they log into the client portal; public
                session bookers must accept them too. Each re-upload is a new version, so past
                acceptances stay traceable to exactly what was agreed at the time.
              </p>
              {profile && profile.termsVersion > 0 ? (
                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">Version {profile.termsVersion}</p>
                    {profile.termsUpdatedAt && (
                      <p className="text-xs text-gray-500">
                        Uploaded {formatDate(profile.termsUpdatedAt, { day: 'numeric', month: 'long', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <Button type="button" variant="outline" size="sm" onClick={viewTerms}>View current</Button>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No Terms &amp; Conditions uploaded yet.</p>
              )}
              <div>
                <input
                  ref={termsFileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={e => { const file = e.target.files?.[0]; if (file) termsMutation.mutate(file); e.target.value = '' }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => termsFileInputRef.current?.click()}
                  disabled={termsMutation.isPending}
                >
                  <Upload size={14} className="mr-1.5" />
                  {termsMutation.isPending ? 'Uploading…' : profile && profile.termsVersion > 0 ? 'Upload new version' : 'Upload PDF'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding tab — logo, colours, header/corner style, font, live preview */}
        <TabsContent value="branding">
          <form onSubmit={e => { e.preventDefault(); brandingMutation.mutate() }} className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start max-w-4xl">
            <div className="space-y-6">
              <Card>
                <CardHeader><CardTitle className="text-base">Logo</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {profile?.logoUrl ? (
                    <div className="flex items-center gap-4">
                      <img
                        src={profile.logoUrl}
                        alt="Company logo"
                        className="h-16 w-auto object-contain rounded border border-gray-200 p-2 bg-white"
                      />
                      <Button type="button" variant="outline" size="sm" className="gap-1.5"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoMutation.isPending}>
                        <Upload size={14} /> {logoMutation.isPending ? 'Uploading…' : 'Replace logo'}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div className="h-16 w-24 rounded border-2 border-dashed border-gray-200 flex items-center justify-center text-xs text-gray-400">
                        No logo
                      </div>
                      <Button type="button" variant="outline" size="sm" className="gap-1.5"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={logoMutation.isPending}>
                        <Upload size={14} /> {logoMutation.isPending ? 'Uploading…' : 'Upload logo'}
                      </Button>
                    </div>
                  )}
                  <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoChange} />
                  <p className="text-xs text-gray-400">PNG, JPG, SVG or WebP. Shown in the client portal header.</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle className="text-base">Branding</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <TextField id="displayName" label="Company display name" value={displayName}
                    onChange={setDisplayName} placeholder="e.g. Acme Training Ltd" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <ColourField label="Primary colour" value={primaryColour} onChange={setPrimaryColour}
                      hint="Header background &amp; main actions" />
                    <ColourField label="Secondary colour" value={secondaryColour} onChange={setSecondaryColour}
                      hint="Accents — active tabs, badges, highlights" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <SegmentedField
                      label="Header style"
                      value={headerStyle}
                      onChange={setHeaderStyle}
                      options={[{ value: 'Dark', label: 'Dark' }, { value: 'Light', label: 'Light' }]}
                      hint="Dark: solid primary-colour bar. Light: pale header with colour as accent — better for pastel brands."
                    />
                    <SegmentedField
                      label="Corner style"
                      value={cornerStyle}
                      onChange={setCornerStyle}
                      options={[{ value: 'Rounded', label: 'Rounded' }, { value: 'Sharp', label: 'Sharp' }]}
                      hint="Applies to buttons, cards and badges across the client portal."
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fontFamily">Font</Label>
                    <select
                      id="fontFamily"
                      value={fontFamily}
                      onChange={e => setFontFamily(e.target.value)}
                      className="h-10 w-full max-w-xs rounded-md border border-gray-200 px-3 text-sm bg-white"
                    >
                      {Object.entries(FONT_OPTIONS).map(([key, opt]) => (
                        <option key={key} value={key}>{opt.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-400">Used across the client portal.</p>
                  </div>
                </CardContent>
              </Card>

              <UnsavedBar visible={isBrandingDirty} onCancel={cancelBranding} isPending={brandingMutation.isPending} />
            </div>

            <div className="lg:sticky lg:top-4 space-y-2">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Preview</p>
              <BrandPreview
                brand={{ primaryColour, secondaryColour, headerStyle, cornerStyle, fontFamily }}
                displayName={displayName}
                logoUrl={profile?.logoUrl}
              />
              <p className="text-xs text-gray-400">Updates as you edit — save to apply to the actual client portal.</p>
            </div>
          </form>
        </TabsContent>

        {/* Invoicing tab — banking & payment */}
        <TabsContent value="invoicing">
          <form onSubmit={e => { e.preventDefault(); invoicingMutation.mutate() }} className="space-y-6 max-w-2xl">
            <Card>
              <CardHeader><CardTitle className="text-base">Tax</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5 max-w-xs">
                  <Label htmlFor="tax-label">Tax name</Label>
                  <Input
                    id="tax-label"
                    value={taxLabel}
                    onChange={e => setTaxLabel(e.target.value)}
                    maxLength={30}
                    placeholder={isUsd ? 'e.g. Sales tax' : 'e.g. VAT'}
                  />
                  <p className="text-xs text-gray-400">
                    What your tax is called on invoices and emails, e.g. VAT, Sales tax or Tax.
                  </p>
                </div>
                <div className="space-y-1.5 max-w-xs">
                  <Label htmlFor="default-vat">Default {taxLabel || 'VAT'} rate (%)</Label>
                  <Input
                    id="default-vat"
                    type="number"
                    min={0}
                    max={100}
                    step={0.01}
                    value={defaultVatRate}
                    onChange={e => setDefaultVatRate(e.target.value)}
                    placeholder="e.g. 20"
                  />
                  <p className="text-xs text-gray-400">
                    Leave blank if you don't charge tax. Applied automatically when a new invoice is generated.
                    {currency === 'GBP' && ' Common UK rates: 20% (standard), 5% (reduced), 0% (zero-rated).'}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Banking &amp; Payment</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField id="bankName" label="Bank name" value={bankName}
                    onChange={setBankName} placeholder="e.g. Barclays" />
                  <TextField id="accountName" label="Account name" value={accountName}
                    onChange={setAccountName} placeholder="Name on the account" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField id="accountNumber" label="Account number" value={accountNumber}
                    onChange={setAccountNumber} placeholder="e.g. 12345678" />
                  {(currency === 'GBP' || sortCode) && (
                    <TextField id="sortCode" label="Sort code" value={sortCode}
                      onChange={setSortCode} placeholder="e.g. 12-34-56" />
                  )}
                  {(isUsd || routingNumber) && (
                    <TextField id="routingNumber" label="Routing number (ABA)" value={routingNumber}
                      onChange={setRoutingNumber} placeholder="e.g. 021000021" />
                  )}
                </div>
                <TextField id="iban" label="IBAN" value={iban}
                  onChange={setIban} placeholder="e.g. GB29 NWBK 6016 1331 9268 19 (optional)" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="paymentTermsDays">Payment terms (days)</Label>
                    <Input
                      id="paymentTermsDays"
                      type="number"
                      min={0}
                      value={paymentTermsDays}
                      onChange={e => setPaymentTermsDays(e.target.value)}
                      placeholder="e.g. 30"
                    />
                  </div>
                  <TextField id="paymentReference" label="Payment reference instructions" value={paymentReference}
                    onChange={setPaymentReference} placeholder="e.g. Please quote invoice number" />
                </div>
                <p className="text-xs text-gray-400">These details will appear on invoices sent to clients.</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Online payment link</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="paymentLink">Payment link URL</Label>
                  <Input
                    id="paymentLink"
                    type="url"
                    value={paymentLink}
                    onChange={e => setPaymentLink(e.target.value)}
                    placeholder="https://buy.stripe.com/… or paypal.me/…"
                  />
                  <p className="text-xs text-gray-400">
                    Paste your Stripe payment link, PayPal.me URL, or GoCardless link. A "Pay now" button will appear on invoices in the client portal.
                    For card payments with automatic reconciliation, use Stripe Connect in the Integrations tab.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Online payments</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Control where customers can pay online — via Stripe Connect (Integrations tab) or your payment link above.
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptInvoicePayments}
                    onChange={e => setAcceptInvoicePayments(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Let clients pay invoices online</p>
                    <p className="text-xs text-gray-500">Shows a "Pay" button on invoices in the client portal.</p>
                  </div>
                </label>

                {acceptInvoicePayments && (
                  <label className="ml-7 flex items-start gap-2.5 cursor-pointer border-l-2 border-gray-100 pl-4">
                    <input
                      type="checkbox"
                      checked={passFeeToCustomerForInvoices}
                      onChange={e => setPassFeeToCustomerForInvoices(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm text-gray-900">Pass the administration fee on to the client</p>
                      <p className="text-xs text-gray-500">Adds the administration fee as an extra line item so the client pays it, instead of it being deducted from what you receive.</p>
                    </div>
                  </label>
                )}

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptSessionPayments}
                    onChange={e => setAcceptSessionPayments(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Take card payments for session bookings</p>
                    <p className="text-xs text-gray-500">Shows a "Pay" option on public session booking confirmations.</p>
                  </div>
                </label>

                {acceptSessionPayments && (
                  <label className="ml-7 flex items-start gap-2.5 cursor-pointer border-l-2 border-gray-100 pl-4">
                    <input
                      type="checkbox"
                      checked={passFeeToCustomerForSessions}
                      onChange={e => setPassFeeToCustomerForSessions(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm text-gray-900">Pass the administration fee on to the attendee</p>
                      <p className="text-xs text-gray-500">Adds the administration fee as an extra line item so the attendee pays it, instead of it being deducted from what you receive.</p>
                    </div>
                  </label>
                )}

                {acceptSessionPayments && (
                  <div className="ml-7 space-y-2 border-l-2 border-gray-100 pl-4">
                    <p className="text-sm font-medium text-gray-900">When should attendees pay?</p>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="radio"
                        name="sessionPaymentTiming"
                        checked={!requireSessionPaymentToBook}
                        onChange={() => setRequireSessionPaymentToBook(false)}
                        className="mt-0.5 w-4 h-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <p className="text-sm text-gray-900">Book now, pay after</p>
                        <p className="text-xs text-gray-500">The place is reserved immediately; payment is offered on the confirmation page.</p>
                      </div>
                    </label>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="radio"
                        name="sessionPaymentTiming"
                        checked={requireSessionPaymentToBook}
                        onChange={() => setRequireSessionPaymentToBook(true)}
                        className="mt-0.5 w-4 h-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                      />
                      <div>
                        <p className="text-sm text-gray-900">Require payment to book</p>
                        <p className="text-xs text-gray-500">The place is only confirmed once paid; the seat is held for 30 minutes during checkout. Requires Stripe payments active.</p>
                      </div>
                    </label>
                  </div>
                )}

                {stripeStatus?.isConnected && !stripeStatus.connection?.chargesEnabled && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Stripe onboarding isn't finished, so card payments aren't active yet. These options take effect once Stripe shows "Payments active" (or set a payment link above).
                  </div>
                )}
              </CardContent>
            </Card>

            <UnsavedBar visible={isInvoicingDirty} onCancel={cancelInvoicing} isPending={invoicingMutation.isPending} />
          </form>
        </TabsContent>

        {/* Certificates tab — director's signature */}
        <TabsContent value="certificates">
          <Card className="max-w-2xl">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <PenLine size={16} /> Director's Signature
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SignatureEditor
                signatureType={profile?.signatureType ?? 'None'}
                signatureText={profile?.signatureText ?? null}
                signatureImageUrl={profile?.signatureImageUrl ?? null}
                isPending={companySignatureMutation.isPending || companySignatureImageMutation.isPending}
                onSave={(type, text) => companySignatureMutation.mutate({ type, text })}
                onUpload={file => companySignatureImageMutation.mutate(file)}
                label="Director's signature"
                hint="Used as a fallback when a trainer has no signature set, and for training records added manually (without a booking)."
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications tab — expiry reminder schedules */}
        <TabsContent value="notifications">
          <form onSubmit={e => { e.preventDefault(); if (isPro) reminderMutation.mutate() }} className="space-y-6 max-w-2xl">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  Expiry Reminder Schedules
                  {!isPro && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">Pro</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <div className={`space-y-6 ${!isPro ? 'pointer-events-none select-none opacity-50' : ''}`}>
                    <p className="text-sm text-gray-500">
                      Configure when reminder emails are sent as certificates approach their expiry date.
                      Defaults are 60, 30 and 14 days before expiry.
                    </p>

                    <DaysTagInput
                      label="Admin alerts (sent to you)"
                      hint="You'll receive an alert from CertSlate at each of these thresholds."
                      days={adminDays}
                      onChange={setAdminDays}
                    />

                    <DaysTagInput
                      label="Client manager reminders (sent to your clients)"
                      hint="Client managers will receive a branded reminder from your company at each of these thresholds."
                      days={clientDays}
                      onChange={setClientDays}
                    />

                    <div className="flex items-center gap-3 pt-1">
                      <Button type="submit" disabled={reminderMutation.isPending}>
                        {reminderMutation.isPending ? 'Saving…' : 'Save reminder schedule'}
                      </Button>
                      {reminderSaved && (
                        <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                          <Check size={14} /> Saved
                        </span>
                      )}
                    </div>
                  </div>

                  {!isPro && (
                    <div
                      className="absolute inset-0 flex items-center justify-center cursor-pointer rounded-lg"
                      onClick={() => setShowProDialog(true)}
                    >
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-white border border-amber-200 rounded-full shadow-sm hover:shadow-md transition-shadow">
                        <Lock size={14} className="text-amber-500" />
                        <span className="text-sm font-medium text-gray-700">Pro feature — click to upgrade</span>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </form>

          <ProUpgradeDialog
            open={showProDialog}
            onClose={() => setShowProDialog(false)}
            featureName="Custom notification schedule"
          />

          {/* Email templates */}
          <Card className="max-w-2xl mt-6">
            <CardHeader>
              <CardTitle className="text-base">Email templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">
                Customise the templates used when sending manual emails from the invoices and compliance pages.
                Variables in <code className="text-xs bg-gray-100 px-1 rounded">{'{{double braces}}'}</code> are substituted automatically.
              </p>
              {(emailTemplates ?? []).map(t => (
                <div key={t.type} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {t.type === 'invoice_reminder' ? 'Payment reminder' : 'Certificate expiry warning'}
                      </p>
                      {t.isCustomised && (
                        <span className="text-xs text-indigo-600 font-medium">Customised</span>
                      )}
                    </div>
                    {editingTemplate !== t.type ? (
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => startEditTemplate(t)}>
                        <PenLine size={13} /> Edit
                      </Button>
                    ) : (
                      <div className="flex items-center gap-2">
                        {templateSaved && (
                          <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                            <Check size={12} /> Saved
                          </span>
                        )}
                        <Button
                          size="sm"
                          onClick={() => templateMutation.mutate({ type: t.type, subject: templateSubject, body: templateBody })}
                          disabled={templateMutation.isPending}
                        >
                          {templateMutation.isPending ? 'Saving…' : 'Save'}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingTemplate(null)}>
                          <X size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                  {editingTemplate === t.type ? (
                    <div className="p-4 space-y-3">
                      <div>
                        <Label className="text-xs text-gray-500 uppercase tracking-wide">Subject</Label>
                        <Input
                          value={templateSubject}
                          onChange={e => setTemplateSubject(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500 uppercase tracking-wide">Body</Label>
                        <textarea
                          value={templateBody}
                          onChange={e => setTemplateBody(e.target.value)}
                          rows={8}
                          className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 space-y-2">
                      <p className="text-xs text-gray-500 font-medium">Subject: <span className="text-gray-700">{t.subject}</span></p>
                      <p className="text-xs text-gray-500 font-medium whitespace-pre-wrap text-gray-700 line-clamp-3">{t.body}</p>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
        {/* Feedback tab */}
        <TabsContent value="photos">
          <PhotoSettingsCard />
        </TabsContent>

        <TabsContent value="live">
          <LiveSettingsCard />
        </TabsContent>

        <TabsContent value="portal">
          <PortalDefaultsCard onOpenFeedback={() => setSearchParams(prev => { prev.set('tab', 'feedback'); return prev })} />
        </TabsContent>

        <TabsContent value="feedback">
          <form onSubmit={e => { e.preventDefault(); feedbackMutation.mutate() }} className="space-y-6 max-w-2xl">
            <Card>
              <CardHeader><CardTitle className="text-base">Feedback settings</CardTitle></CardHeader>
              <CardContent className="space-y-5">
                <p className="text-sm text-gray-500">
                  Configure how client feedback is collected and displayed after completed bookings.
                </p>

                <div className="space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={feedbackEnabled}
                      onChange={e => setFeedbackEnabled(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Enable feedback</p>
                      <p className="text-xs text-gray-500">Allow client managers to rate and comment on completed bookings.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={feedbackTrainerVisible}
                      onChange={e => setFeedbackTrainerVisible(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Trainer visibility</p>
                      <p className="text-xs text-gray-500">Trainers can see ratings and comments left for sessions they delivered.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={feedbackAllowEdit}
                      onChange={e => setFeedbackAllowEdit(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Allow clients to edit feedback</p>
                      <p className="text-xs text-gray-500">Client managers can update their rating or comment after submitting.</p>
                    </div>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={feedbackNotifyClient}
                      onChange={e => setFeedbackNotifyClient(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Notify clients to leave feedback</p>
                      <p className="text-xs text-gray-500">Send a notification to client managers when a booking is completed, prompting them to submit feedback.</p>
                    </div>
                  </label>
                </div>

                <div className="space-y-1.5 pt-2 border-t border-gray-100">
                  <Label htmlFor="feedbackWindow">Feedback window (days)</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="feedbackWindow"
                      type="number"
                      min="1"
                      value={feedbackWindowDays}
                      onChange={e => setFeedbackWindowDays(e.target.value)}
                      placeholder="No limit"
                      className="w-36"
                    />
                    <p className="text-xs text-gray-400">
                      Days after the booking date during which feedback can be submitted. Leave blank for no limit.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-1">
                  <Button type="submit" disabled={feedbackMutation.isPending}>
                    {feedbackMutation.isPending ? 'Saving…' : 'Save feedback settings'}
                  </Button>
                  {feedbackSaved && (
                    <span className="flex items-center gap-1.5 text-sm text-green-600 font-medium">
                      <Check size={14} /> Saved
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          </form>
        </TabsContent>
        {/* Venues tab */}
        <TabsContent value="venues">
          <VenuesPage />
        </TabsContent>

        {/* Integrations tab */}
        <TabsContent value="integrations">
          <div className="space-y-6 max-w-2xl">

            {/* Xero callback result banners */}
            {xeroCallbackResult === 'connected' && (
              <div className="rounded-xl border border-green-200 bg-green-50 px-5 py-3 flex items-center gap-3 text-sm text-green-800">
                <Check size={16} className="text-green-600 shrink-0" />
                Successfully connected to Xero.
              </div>
            )}
            {xeroCallbackResult === 'error' && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-5 py-3 text-sm text-red-800">
                Failed to connect to Xero{xeroCallbackMessage ? `: ${xeroCallbackMessage}` : ''}. Please try again.
              </div>
            )}

            {/* Xero */}
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-base">Xero</CardTitle>
                  <span className="text-[10px] font-bold bg-amber-400 text-amber-900 rounded px-1.5 py-0.5 leading-4">BETA</span>
                  {!isPro && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">Pro</span>}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Push invoices from CertSlate to your Xero account as draft invoices.
                </p>
              </CardHeader>
              <CardContent className="space-y-5">
                {!xeroStatus?.isConfigured && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Xero integration is not yet configured on this server. Contact CertSlate support to enable it.
                  </div>
                )}

                {xeroStatus?.isConnected && xeroStatus.connection ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                      <Check size={16} className="text-green-600 shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-green-800">Connected to {xeroStatus.connection.organisationName}</p>
                        <p className="text-xs text-green-600 mt-0.5">
                          Connected {formatDate(xeroStatus.connection.connectedAt)}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="space-y-1.5">
                        <Label htmlFor="xero-account-code">Account code</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id="xero-account-code"
                            value={xeroAccountCode}
                            onChange={e => setXeroAccountCode(e.target.value)}
                            placeholder="200"
                            className="max-w-xs"
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => xeroSettingsMutation.mutate()}
                            disabled={xeroSettingsMutation.isPending}
                          >
                            {xeroSettingsMutation.isPending ? 'Saving…' : 'Save'}
                          </Button>
                          {xeroAccountSaved && (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <Check size={13} /> Saved
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400">
                          The Xero chart of accounts code for invoice line items (e.g. "200" for Sales income). Find this in Xero → Accounting → Chart of Accounts.
                        </p>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="xero-output-tax-type">Sales tax type</Label>
                        <Input
                          id="xero-output-tax-type"
                          value={xeroOutputTaxType}
                          onChange={e => setXeroOutputTaxType(e.target.value)}
                          placeholder={currency === 'GBP' ? 'Optional (UK rates are detected)' : 'e.g. TAX001'}
                          className="max-w-xs"
                        />
                        <p className="text-xs text-gray-400">
                          The Xero tax rate code applied to invoices that charge tax. Required for {currency === 'GBP' ? 'non-UK tax rates' : 'invoices that charge tax'}.
                          Find the code in Xero → Accounting → Tax Rates. Use the Save button above to save.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => { if (confirm('Disconnect from Xero?')) disconnectXeroMutation.mutate() }}
                        disabled={disconnectXeroMutation.isPending}
                      >
                        <Unlink size={13} />
                        {disconnectXeroMutation.isPending ? 'Disconnecting…' : 'Disconnect from Xero'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => connectXeroMutation.mutate()}
                        disabled={connectXeroMutation.isPending}
                      >
                        <RefreshCw size={13} />
                        Reconnect
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    className="gap-2"
                    onClick={() => isPro ? connectXeroMutation.mutate() : setProFeature('Xero integration')}
                    disabled={connectXeroMutation.isPending || (isPro && !xeroStatus?.isConfigured)}
                  >
                    <Link2 size={15} />
                    {connectXeroMutation.isPending ? 'Redirecting to Xero…' : 'Connect to Xero'}
                    <ExternalLink size={13} className="opacity-60" />
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* ── Stripe Connect ── */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Stripe Connect <span className="ml-1.5 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">BETA</span>{!isPro && <span className="ml-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-600 bg-amber-50 border border-amber-200 rounded px-1 py-0.5">Pro</span>}</CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Accept card payments directly from clients. Payments go straight to your Stripe account — automatic reconciliation when a client pays an invoice.
                </p>
              </CardHeader>
              <CardContent className="space-y-5">
                {!stripeStatus?.isConfigured && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Stripe Connect is not yet configured on this server. Contact CertSlate support to enable it.
                  </div>
                )}

                {stripeCallbackResult === 'onboarded' && (
                  <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                    Returned from Stripe. Your onboarding status is shown below.
                  </div>
                )}
                {stripeCallbackResult === 'refresh' && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    Your Stripe onboarding link expired. Click "Finish Stripe setup" to continue.
                  </div>
                )}

                {stripeStatus?.isConnected && stripeStatus.connection ? (
                  <div className="space-y-4">
                    {stripeStatus.connection.chargesEnabled ? (
                      <div className="flex items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                        <Check size={16} className="text-green-600 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-green-800">Payments active</p>
                          <p className="text-xs text-green-600 mt-0.5 font-mono">{stripeStatus.connection.stripeAccountId}</p>
                          <p className="text-xs text-green-600 mt-0.5">
                            Card payments enabled — clients can pay invoices and session bookings online.
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                        <RefreshCw size={16} className="text-amber-600 shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-amber-800">Setup incomplete</p>
                          <p className="text-xs text-amber-700 mt-0.5 font-mono">{stripeStatus.connection.stripeAccountId}</p>
                          <p className="text-xs text-amber-700 mt-0.5">
                            Stripe still needs more details before you can accept payments. Finish onboarding below.
                          </p>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      {!stripeStatus.connection.chargesEnabled && (
                        <Button
                          type="button"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => connectStripeMutation.mutate()}
                          disabled={connectStripeMutation.isPending}
                        >
                          <ExternalLink size={13} />
                          {connectStripeMutation.isPending ? 'Redirecting to Stripe…' : 'Finish Stripe setup'}
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => { if (confirm('Disconnect from Stripe? Clients will no longer be able to pay online.')) disconnectStripeMutation.mutate() }}
                        disabled={disconnectStripeMutation.isPending}
                      >
                        <Unlink size={13} />
                        {disconnectStripeMutation.isPending ? 'Disconnecting…' : 'Disconnect'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    type="button"
                    className="gap-2"
                    onClick={() => isPro ? connectStripeMutation.mutate() : setProFeature('Stripe payments')}
                    disabled={connectStripeMutation.isPending || (isPro && !stripeStatus?.isConfigured)}
                  >
                    <Link2 size={15} />
                    {connectStripeMutation.isPending ? 'Redirecting to Stripe…' : 'Set up payments'}
                    <ExternalLink size={13} className="opacity-60" />
                  </Button>
                )}
              </CardContent>
            </Card>

            <ProUpgradeDialog
              open={!!proFeature}
              onClose={() => setProFeature(null)}
              featureName={proFeature ?? ''}
            />

          </div>
        </TabsContent>

        {/* Import tab — bulk roster import */}
        <TabsContent value="import">
          <RosterImporter
            upload={(file, dryRun) => adminApi.importRoster(file, dryRun).then(r => r.data)}
            onImported={() => queryClient.invalidateQueries({ queryKey: ['clients'] })}
          />
        </TabsContent>

      </Tabs>
    </div>
  )
}
